import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  Injector,
  OnInit,
  ViewChild,
  afterNextRender,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
import { MatButtonModule } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Modal } from '@spirit-testing/component-library';
import { ModalEscapeGuardDirective } from '../modal-escape-guard/modal-escape-guard.directive';
import {
  ProjectEnvironment,
  ProjectEnvironmentError,
  ProjectEnvironmentRequest,
} from '../../models/project-environment.model';
import { Testdatenblatt } from '../../models/testdatenblatt.model';
import { ProjectEnvironmentService } from '../../services/project-environment.service';
import { TestdatenblattService } from '../../services/testdatenblatt.service';
import { FIELD_LENGTHS } from '../../shared/field-lengths';

type Ladestatus = 'laedt' | 'geladen' | 'fehler';
type Feld = 'name' | 'url' | 'defaultTdbId';
type Textfeld = 'name' | 'url' | 'username' | 'password';

interface Entwurf {
  name: string;
  url: string;
  username: string;
  password: string;
  defaultTdbId: string | null;
  clearPassword: boolean;
}

interface BlattOption {
  id: string;
  /** `null`: gespeichertes Blatt, dessen Namen die Karte nicht kennt. */
  label: string | null;
}

const I18N = 'projectForm.umgebungen';
const NEU = 'neu';
const HOECHSTZAHL = 20;
const URL_MIT_SCHEMA = /^https?:\/\//i;
const LEERER_ENTWURF: Entwurf = {
  name: '',
  url: '',
  username: '',
  password: '',
  defaultTdbId: null,
  clearPassword: false,
};
const NEUE_ZEILE: ProjectEnvironment = {
  id: NEU,
  name: '',
  url: null,
  position: Number.MAX_SAFE_INTEGER,
  username: null,
  passwordSet: false,
  defaultTdbId: null,
};
/** 400 mit `field`: Meldung am Feld (Schnittstelle NEX-2283). */
const FEHLER_AM_FELD: Record<string, { feld: Feld; schluessel: string }> = {
  name: { feld: 'name', schluessel: 'nameLeer' },
  url: { feld: 'url', schluessel: 'urlUngueltig' },
  defaultTdbId: { feld: 'defaultTdbId', schluessel: 'tdbUngueltig' },
};

function entwurfAus(u: ProjectEnvironment): Entwurf {
  return {
    ...LEERER_ENTWURF,
    name: u.name,
    url: u.url ?? '',
    username: u.username ?? '',
    defaultTdbId: u.defaultTdbId,
  };
}

function sortiert(liste: ProjectEnvironment[]): ProjectEnvironment[] {
  return [...liste].sort((a, b) => a.position - b.position || a.name.localeCompare(b.name));
}

/**
 * Karte „Testumgebungen“ im Projektformular (NEX-2283, Design page-projekt-neu.jsx:86-185). Lädt ihre Liste
 * selbst und speichert je Zeile direkt über die API, nie über das Projekt. Die Karte liegt im `<form>` des
 * Projektformulars: Alle Knöpfe sind `type="button"`, und die Eingabetaste speichert die Zeile.
 */
@Component({
  selector: 'app-testumgebungen-karte',
  imports: [TranslatePipe, MatButtonModule, MatIcon, Modal, ModalEscapeGuardDirective],
  templateUrl: './testumgebungen-karte.component.html',
  styleUrl: './testumgebungen-karte.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TestumgebungenKarteComponent implements OnInit {
  private readonly service = inject(ProjectEnvironmentService);
  private readonly testdatenblattService = inject(TestdatenblattService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

  readonly projectId = input.required<string>();
  readonly nurLesen = input(false);
  /** Offene Zeile mit ungespeicherten Eingaben: Das Projektformular fragt dann beim Verlassen nach. */
  readonly ungespeichertGemeldet = output<boolean>();

  @ViewChild('entfernenModal', { static: true }) private entfernenModal!: Modal;

  readonly fieldLengths = FIELD_LENGTHS;
  readonly neu = NEU;

  readonly umgebungen = signal<ProjectEnvironment[]>([]);
  readonly ladestatus = signal<Ladestatus>('laedt');
  readonly blaetter = signal<Testdatenblatt[]>([]);
  readonly blaetterStatus = signal<Ladestatus>('laedt');
  readonly offen = signal<string | null>(null);
  readonly entwurf = signal<Entwurf>(LEERER_ENTWURF);
  readonly speichert = signal(false);
  private readonly serverFehler = signal<Partial<Record<Feld, string>>>({});
  readonly entfernenZiel = signal<ProjectEnvironment | null>(null);
  readonly entfernenLaeuft = signal(false);
  /** Wartet auf „Verwerfen“, weil die offene Zeile ungespeicherte Eingaben hat. */
  readonly verwerfenAktion = signal<(() => void) | null>(null);

  readonly zeilen = computed(() => (this.offen() === NEU ? [...this.umgebungen(), NEUE_ZEILE] : this.umgebungen()));
  private readonly offeneUmgebung = computed(() => this.umgebungen().find((u) => u.id === this.offen()) ?? null);
  private readonly blattNamen = computed(
    () => new Map(this.blaetter().map((b) => [b.id, `${b.key} · ${b.name}`] as const)),
  );

  private readonly urlUngueltig = computed(() => {
    const url = this.entwurf().url.trim();
    return url !== '' && !URL_MIT_SCHEMA.test(url);
  });
  readonly fehler = computed<Partial<Record<Feld, string>>>(() =>
    this.urlUngueltig() ? { ...this.serverFehler(), url: 'urlUngueltig' } : this.serverFehler(),
  );
  private readonly geaendert = computed(() => {
    const u = this.offeneUmgebung();
    const basis = u ? entwurfAus(u) : LEERER_ENTWURF;
    const e = this.entwurf();
    return (
      e.name.trim() !== basis.name ||
      e.url.trim() !== basis.url ||
      e.username.trim() !== basis.username ||
      e.defaultTdbId !== basis.defaultTdbId ||
      e.password.trim() !== '' ||
      e.clearPassword
    );
  });
  private readonly ungespeichert = computed(() => this.offen() !== null && this.geaendert());
  readonly speichernGesperrt = computed(
    () => this.speichert() || !this.entwurf().name.trim() || this.urlUngueltig() || !this.geaendert(),
  );
  readonly hinzufuegenGesperrt = computed(() => this.umgebungen().length >= HOECHSTZAHL || this.speichert());
  readonly passwortHinterlegt = computed(() => !!this.offeneUmgebung()?.passwordSet && !this.entwurf().clearPassword);

  readonly blattOptionen = computed<BlattOption[]>(() => {
    if (this.blaetterStatus() === 'laedt') return [];
    const namen = this.blattNamen();
    const optionen: BlattOption[] = this.blaetter().map((b) => ({ id: b.id, label: namen.get(b.id) ?? null }));
    const gespeichert = this.offeneUmgebung()?.defaultTdbId;
    if (gespeichert && !namen.has(gespeichert)) optionen.push({ id: gespeichert, label: null });
    return optionen;
  });

  constructor() {
    effect(() => this.ungespeichertGemeldet.emit(this.ungespeichert()));
  }

  ngOnInit(): void {
    this.laden();
    this.testdatenblattService
      .list(this.projectId())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (blaetter) => {
          this.blaetter.set(blaetter);
          this.blaetterStatus.set('geladen');
        },
        error: () => this.blaetterStatus.set('fehler'),
      });
  }

  laden(): void {
    this.ladestatus.set('laedt');
    this.service
      .list(this.projectId())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (liste) => {
          this.umgebungen.set(sortiert(liste));
          this.ladestatus.set('geladen');
        },
        error: () => this.ladestatus.set('fehler'),
      });
  }

  blattName(id: string): string | null {
    return this.blattNamen().get(id) ?? null;
  }

  kopfId(id: string): string {
    return `umgebung-kopf-${id}`;
  }

  formularId(id: string): string {
    return `umgebung-formular-${id}`;
  }

  feldId(id: string, feld: string): string {
    return `umgebung-${id}-${feld}`;
  }

  umschalten(id: string): void {
    if (this.speichert()) return;
    this.nachRueckfrage(this.offen() === id ? () => this.schliessen() : () => this.oeffnen(id));
  }

  perTaste(event: Event, id: string): void {
    event.preventDefault();
    this.umschalten(id);
  }

  hinzufuegen(): void {
    if (this.hinzufuegenGesperrt()) return;
    this.nachRueckfrage(() => {
      this.oeffnen(NEU);
      this.fokussieren(`[id="${this.feldId(NEU, 'name')}"]`);
    });
  }

  verwerfenBestaetigen(): void {
    const aktion = this.verwerfenAktion();
    const vorher = this.offen();
    this.verwerfenAktion.set(null);
    aktion?.();
    const ziel = this.offen() ?? vorher;
    if (ziel && ziel !== NEU) {
      this.fokussieren(`[id="${this.kopfId(ziel)}"]`);
    } else if (ziel === NEU && this.offen() === null) {
      this.fokussieren('[data-testid="umgebung-hinzufuegen"]');
    }
  }

  verwerfenAbbrechen(): void {
    this.verwerfenAktion.set(null);
    const id = this.offen();
    if (id) this.fokussieren(`[id="${this.kopfId(id)}"]`);
  }

  beimSchliessenDerVerwerfenFrage(sichtbar: boolean): void {
    if (!sichtbar) this.verwerfenAbbrechen();
  }

  aendern(feld: Textfeld, wert: string): void {
    this.entwurf.update((e) => ({ ...e, [feld]: wert }));
    if (feld === 'name' || feld === 'url') this.fehlerEntfernen(feld);
  }

  blattWaehlen(wert: string): void {
    this.entwurf.update((e) => ({ ...e, defaultTdbId: wert || null }));
    this.fehlerEntfernen('defaultTdbId');
  }

  passwortEntfernen(): void {
    this.entwurf.update((e) => ({ ...e, password: '', clearPassword: true }));
  }

  perEingabetaste(event: Event): void {
    event.preventDefault();
    this.speichern();
  }

  abbrechen(): void {
    const id = this.offen();
    if (!id || this.speichert()) return;
    this.schliessen();
    this.fokussieren(id === NEU ? '[data-testid="umgebung-hinzufuegen"]' : `[id="${this.kopfId(id)}"]`);
  }

  speichern(): void {
    const id = this.offen();
    if (!id || this.nurLesen() || this.speichernGesperrt()) return;
    const e = this.entwurf();
    const body: ProjectEnvironmentRequest = {
      name: e.name.trim(),
      url: e.url.trim() || null,
      username: e.username.trim() || null,
      password: e.password.trim() ? e.password : null,
      defaultTdbId: e.defaultTdbId,
    };
    const anfrage =
      id === NEU
        ? this.service.create(this.projectId(), body)
        : this.service.update(this.projectId(), id, { ...body, clearPassword: e.clearPassword && !body.password });

    this.speichert.set(true);
    this.serverFehler.set({});
    anfrage.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (gespeichert) => {
        this.speichert.set(false);
        this.umgebungen.update((liste) =>
          id === NEU ? sortiert([...liste, gespeichert]) : liste.map((u) => (u.id === id ? gespeichert : u)),
        );
        this.schliessen();
        this.melden('gespeichert', true);
        this.fokussieren(`[id="${this.kopfId(gespeichert.id)}"]`);
      },
      error: (fehler: HttpErrorResponse) => {
        this.speichert.set(false);
        this.speicherfehlerZeigen(fehler);
      },
    });
  }

  entfernenFragen(umgebung: ProjectEnvironment): void {
    this.entfernenZiel.set(umgebung);
    this.fokussieren('[data-testid="umgebung-entfernen-abbrechen"]');
  }

  entfernenAbbrechen(): void {
    if (this.entfernenLaeuft()) return;
    this.entfernenZiel.set(null);
    this.fokussieren('[data-testid="umgebung-entfernen"]');
  }

  /**
   * `lib-modal` blendet sich beim Klick auf den Hintergrund selbst aus, bevor es meldet.
   * Während des Entfernens bleibt die Rückfrage offen, deshalb hier wieder einblenden.
   */
  beimSchliessenDurchModal(sichtbar: boolean): void {
    if (sichtbar) return;
    if (this.entfernenLaeuft()) {
      this.entfernenModal.visible = true;
      return;
    }
    this.entfernenAbbrechen();
  }

  entfernenBestaetigen(): void {
    const ziel = this.entfernenZiel();
    if (!ziel || this.entfernenLaeuft()) return;
    this.entfernenLaeuft.set(true);
    this.service
      .remove(this.projectId(), ziel.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.entfernenBeenden();
          this.umgebungen.update((liste) => liste.filter((u) => u.id !== ziel.id));
          this.schliessen();
          this.fokussieren('[data-testid="umgebung-hinzufuegen"]');
        },
        error: (fehler: HttpErrorResponse) => {
          this.entfernenBeenden();
          if (fehler.status === 404) {
            this.schliessen();
            this.laden();
            return;
          }
          this.melden(fehler.status === 403 ? 'fehler.keinRecht' : 'fehler.entfernen');
          this.fokussieren('[data-testid="umgebung-entfernen"]');
        },
      });
  }

  private entfernenBeenden(): void {
    this.entfernenLaeuft.set(false);
    this.entfernenZiel.set(null);
  }

  private nachRueckfrage(aktion: () => void): void {
    if (!this.ungespeichert()) {
      aktion();
      return;
    }
    this.verwerfenAktion.set(aktion);
    this.fokussieren('[data-testid="umgebung-verwerfen-abbrechen"]');
  }

  private oeffnen(id: string): void {
    const umgebung = this.umgebungen().find((u) => u.id === id);
    this.entwurf.set(umgebung ? entwurfAus(umgebung) : LEERER_ENTWURF);
    this.serverFehler.set({});
    this.offen.set(id);
  }

  private schliessen(): void {
    this.offen.set(null);
    this.serverFehler.set({});
  }

  private fehlerEntfernen(feld: Feld): void {
    if (!this.serverFehler()[feld]) return;
    this.serverFehler.update((alt) => {
      const rest = { ...alt };
      delete rest[feld];
      return rest;
    });
  }

  private speicherfehlerZeigen(fehler: HttpErrorResponse): void {
    const body = (fehler.error ?? null) as ProjectEnvironmentError | null;
    const amFeld = body?.field ? FEHLER_AM_FELD[body.field] : undefined;
    if (fehler.status === 404) {
      this.melden('fehler.nichtGefunden');
      this.schliessen();
      this.laden();
    } else if (fehler.status === 409) {
      this.serverFehler.set({ name: 'nameVergeben' });
    } else if (fehler.status === 400 && amFeld) {
      this.serverFehler.set({ [amFeld.feld]: amFeld.schluessel });
    } else if (fehler.status === 400 && body?.code === 'ENVIRONMENT_LIMIT') {
      this.melden('fehler.limit');
    } else {
      this.melden(fehler.status === 403 ? 'fehler.keinRecht' : 'fehler.speichern');
    }
  }

  private melden(schluessel: string, erfolg = false): void {
    this.snackBar.open(this.translate.instant(`${I18N}.${schluessel}`), undefined, {
      panelClass: erfolg ? 'snackbar-success' : 'snackbar-error',
    });
  }

  private fokussieren(selektor: string): void {
    afterNextRender(() => this.host.nativeElement.querySelector<HTMLElement>(selektor)?.focus(), {
      injector: this.injector,
    });
  }
}
